import { Controller } from "@hotwired/stimulus"
import { toast } from "../utils/toast"

// Connects to data-controller="filter-drawer"
export default class extends Controller {
  static targets = [
    "filterForm", "filterRows", "rowTemplate", "applyButton",
    "standardPanel", "cascadePanel", "standardTab", "cascadeTab", "cascadeRows", "cascadeRowTemplate",
    "savedFiltersSection", "savedFiltersList", "savedFiltersEmpty", "savedFiltersMenu",
    "saveFilterModal", "saveFilterLabel", "saveFilterHint", "deleteFilterModal"
  ]
  static values = {
    basePath: String,
    filterDrawerId: String,
    operators: Object,
    columnTypes: Object,
    filterType: String,
    savedFiltersPath: String,
    mode: String
  }

  connect() {
    this.operatorsByType = this.operatorsValue || {}
    this.columnTypes = this.columnTypesValue || {}
    this.activeMode = this.hasModeValue ? this.modeValue : "cascade"
    this.toggleValueInputs()
    if (this.hasFilterTypeValue && this.filterTypeValue && this.hasSavedFiltersPathValue && this.savedFiltersPathValue) {
      this.fetchSavedFilters()
    }
  }

  addRow() {
    const template = this.rowTemplateTarget
    if (!template) return

    const content = template.innerHTML
    const nextIndex = this.filterRowsTarget.querySelectorAll("[data-filter-row]").length
    const newRowHtml = content.replace(/INDEX_PLACEHOLDER/g, nextIndex)

    this.filterRowsTarget.insertAdjacentHTML("beforeend", newRowHtml)

    // Update operators for the new row based on first column
    const newRow = this.filterRowsTarget.lastElementChild
    const columnSelect = newRow?.querySelector("[data-filter-column]")
    if (columnSelect) {
      this.updateOperatorsForRow(newRow, columnSelect.value)
    }
  }

  removeRow(event) {
    const row = event.target.closest("[data-filter-row]")
    if (!row) return

    const rows = this.filterRowsTarget.querySelectorAll("[data-filter-row]")
    if (rows.length <= 1) return

    row.remove()
    this.renumberRows()
  }

  switchToStandard() {
    this.activeMode = "standard"
    if (this.hasStandardPanelTarget) this.standardPanelTarget.classList.remove("hidden")
    if (this.hasCascadePanelTarget) this.cascadePanelTarget.classList.add("hidden")
    if (this.hasStandardTabTarget) this.standardTabTarget.classList.add("tab-active")
    if (this.hasCascadeTabTarget) this.cascadeTabTarget.classList.remove("tab-active")
    if (this.hasStandardTabTarget) this.standardTabTarget.setAttribute("aria-selected", "true")
    if (this.hasCascadeTabTarget) this.cascadeTabTarget.setAttribute("aria-selected", "false")
  }

  switchToCascade() {
    this.activeMode = "cascade"
    if (this.hasStandardPanelTarget) this.standardPanelTarget.classList.add("hidden")
    if (this.hasCascadePanelTarget) this.cascadePanelTarget.classList.remove("hidden")
    if (this.hasStandardTabTarget) this.standardTabTarget.classList.remove("tab-active")
    if (this.hasCascadeTabTarget) this.cascadeTabTarget.classList.add("tab-active")
    if (this.hasStandardTabTarget) this.standardTabTarget.setAttribute("aria-selected", "false")
    if (this.hasCascadeTabTarget) this.cascadeTabTarget.setAttribute("aria-selected", "true")
  }

  addCascadeRow() {
    if (!this.hasCascadeRowTemplateTarget || !this.hasCascadeRowsTarget) return
    const template = this.cascadeRowTemplateTarget
    const content = template.innerHTML
    const nextIndex = this.cascadeRowsTarget.querySelectorAll("[data-cascade-filter-row]").length
    const newRowHtml = content.replace(/INDEX_PLACEHOLDER/g, nextIndex)
    this.cascadeRowsTarget.insertAdjacentHTML("beforeend", newRowHtml)
  }

  removeCascadeRow(event) {
    const row = event.target.closest("[data-cascade-filter-row]")
    if (!row || !this.hasCascadeRowsTarget) return

    const rows = this.cascadeRowsTarget.querySelectorAll("[data-cascade-filter-row]")
    if (rows.length <= 1) return

    row.remove()
    this.renumberCascadeRows()
  }

  renumberCascadeRows() {
    if (!this.hasCascadeRowsTarget) return
    const rows = this.cascadeRowsTarget.querySelectorAll("[data-cascade-filter-row]")
    rows.forEach((row, index) => {
      row.querySelectorAll("[name^='filter[']").forEach((input) => {
        const name = input.getAttribute("name")
        if (name) {
          input.setAttribute("name", name.replace(/filter\[\d+\]/, `filter[${index}]`))
        }
      })
    })
  }

  updateOperators(event) {
    const row = event.target.closest("[data-filter-row]")
    if (!row) return

    const columnSelect = event.target
    this.updateOperatorsForRow(row, columnSelect.value)
  }

  updateOperatorsForRow(row, columnValue) {
    const type = this.columnTypes[columnValue] || "string"
    const operators = this.operatorsByType[type] || this.operatorsByType["string"] || []
    const operatorSelect = row.querySelector("[data-filter-operator]")
    if (!operatorSelect) return

    const currentValue = operatorSelect.value
    operatorSelect.innerHTML = operators.map((opt) =>
      `<option value="${opt.value}">${opt.label}</option>`
    ).join("")

    if (operators.some((o) => o.value === currentValue)) {
      operatorSelect.value = currentValue
    } else if (operators.length > 0) {
      operatorSelect.value = operators[0].value
    }

    this.toggleValueInputForRow(row)
  }

  toggleValueInputs() {
    if (!this.hasFilterRowsTarget) return
    this.filterRowsTarget.querySelectorAll("[data-filter-row]").forEach((row) => {
      this.toggleValueInputForRow(row)
    })
  }

  toggleValueInputForRow(row) {
    const operatorSelect = row.querySelector("[data-filter-operator]")
    const valueCell = row.querySelector("[data-filter-value-cell]")
    if (operatorSelect && valueCell) {
      valueCell.style.display = ["blank", "present"].includes(operatorSelect.value) ? "none" : ""
    }
  }

  toggleValueInputForEvent(event) {
    const row = event.target.closest("[data-filter-row]")
    if (row) this.toggleValueInputForRow(row)
  }

  renumberRows() {
    const rows = this.filterRowsTarget.querySelectorAll("[data-filter-row]")
    rows.forEach((row, index) => {
      row.querySelectorAll("[name^='filter[']").forEach((input) => {
        const name = input.getAttribute("name")
        if (name) {
          input.setAttribute("name", name.replace(/filter\[\d+\]/, `filter[${index}]`))
        }
      })
    })
  }

  applyFilters() {
    const form = this.filterFormTarget
    if (!form) return

    const url = new URL(window.location.href)
    url.searchParams.forEach((_, key) => {
      if (key === "filters" || key.startsWith("filter[") || key === "filter_mode") url.searchParams.delete(key)
    })

    const filtersArray = this.activeMode === "cascade" ? this.collectCascadeFiltersFromForm() : this.collectStandardFiltersFromForm()

    if (filtersArray.length > 0) {
      const encoded = btoa(unescape(encodeURIComponent(JSON.stringify({ filters: filtersArray }))))
      url.searchParams.set("filters", encoded)
    }
    url.searchParams.set("filter_mode", this.activeMode === "cascade" ? "cascade" : "standard")

    url.searchParams.delete("page")

    // Show loading state (daisyUI: https://daisyui.com/components/button/#button-with-loading-spinner)
    if (this.hasApplyButtonTarget) {
      const btn = this.applyButtonTarget
      btn.disabled = true
      btn.innerHTML = '<span class="loading loading-spinner"></span>Applying...'
    }

    // Allow DOM to update before navigating so user sees loading state
    requestAnimationFrame(() => {
      window.location.href = url.toString()
    })
  }

  resetFilters() {
    const url = new URL(window.location.href)
    url.searchParams.delete("filters")
    url.searchParams.delete("filter_mode")
    url.searchParams.forEach((_, key) => {
      if (key.startsWith("filter[")) url.searchParams.delete(key)
    })
    url.searchParams.delete("page")
    window.location.href = url.toString()
  }

  collectStandardFiltersFromForm() {
    const container = this.filterRowsTarget
    if (!container) return []
    const filterParams = {}
    container.querySelectorAll("[data-filter-row]").forEach((row) => {
      const colSelect = row.querySelector("[data-filter-column]")
      const opSelect = row.querySelector("[data-filter-operator]")
      const valueEl = row.querySelector("[data-filter-value]")
      const logicSelect = row.querySelector("select[name*='[logic]']")
      const colName = colSelect?.getAttribute("name")
      const match = colName?.match(/^filter\[(\d+)\]\[column\]$/)
      if (!match) return
      const index = match[1]
      const column = colSelect?.value
      const op = opSelect?.value
      if (!column || !op) return
      const value = valueEl?.value ?? ""
      const logic = logicSelect?.value ?? "and"
      if (!["blank", "present"].includes(op) && !value) return
      filterParams[index] = { column, op, value, logic }
    })
    return Object.entries(filterParams)
      .sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
      .map(([, params]) => params)
  }

  collectCascadeFiltersFromForm() {
    const container = this.cascadeRowsTarget
    if (!container) return []
    const filterParams = {}
    container.querySelectorAll("[data-cascade-filter-row]").forEach((row) => {
      const colSelect = row.querySelector("[data-cascading-filter-column]")
      const colName = colSelect?.getAttribute("name")
      const colMatch = colName?.match(/^filter\[(\d+)\]\[column\]$/)
      if (!colMatch) return
      const index = colMatch[1]
      const column = colSelect?.value
      if (!column) return
      const textInput = row.querySelector("[data-cascading-filter-text]")
      const valueSelect = row.querySelector("[data-cascading-filter-select]")
      const valueInputs = row.querySelectorAll("[data-cascading-value-input]")
      const checkedBoxes = row.querySelectorAll(".cascade-option:checked")
      let values = []
      if (textInput?.value) {
        values = [textInput.value]
      } else if (valueSelect) {
        values = Array.from(valueSelect.selectedOptions).map((opt) => opt.value).filter(Boolean)
      } else if (checkedBoxes.length) {
        values = Array.from(checkedBoxes).map((cb) => cb.value).filter(Boolean)
      } else {
        values = Array.from(valueInputs).map((inp) => inp.value).filter(Boolean)
      }
      if (values.length === 0) return
      if (!filterParams[index]) filterParams[index] = {}
      filterParams[index].column = column
      filterParams[index].op = "eq"
      filterParams[index].value = values.length === 1 ? values[0] : values
    })
    return Object.entries(filterParams)
      .sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
      .map(([, params]) => params)
  }

  close() {
    const toggle = document.getElementById(this.filterDrawerIdValue)
    if (toggle) toggle.checked = false
  }

  // Saved filters
  async fetchSavedFilters() {
    if (!this.hasSavedFiltersPathValue || !this.filterTypeValue) return
    const url = `${this.savedFiltersPathValue}?filter_type=${encodeURIComponent(this.filterTypeValue)}`
    try {
      const res = await fetch(url, {
        headers: { "Accept": "application/json", "X-Requested-With": "XMLHttpRequest" }
      })
      if (res.ok) {
        const data = await res.json()
        this.renderSavedFilters(data)
      }
    } catch (_err) {
      this.renderSavedFilters([])
    }
  }

  renderSavedFilters(filters) {
    if (!this.hasSavedFiltersMenuTarget || !this.hasSavedFiltersEmptyTarget) return
    this.savedFiltersEmptyTarget.classList.toggle("hidden", filters.length > 0)
    this.savedFiltersMenuTarget.classList.toggle("hidden", filters.length === 0)
    this.savedFiltersMenuTarget.innerHTML = filters.map((sf) => `
      <li class="py-2">
        <div class="flex items-center gap-4 w-full">
          <button type="button" class="btn btn-sm flex-1 min-w-0 text-left truncate" data-action="click->filter-drawer#loadSavedFilter" data-encoded-filters="${sf.encoded_filters}" data-filter-mode="${sf.filter_mode || ""}">
            ${this.escapeHtml(sf.label)}
          </button>
          <button type="button" class="btn btn-ghost btn-sm btn-square text-error shrink-0" data-action="click->filter-drawer#deleteSavedFilter" data-saved-filter-id="${sf.id}" aria-label="Delete filter">
            <span class="icon-[lucide--trash-2] size-4"></span>
          </button>
        </div>
      </li>
    `).join("")
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  saveFilter() {
    const filtersArray = this.collectFiltersFromForm()
    if (filtersArray.length === 0) {
      toast("Add at least one filter with a column and value before saving.", { type: "warning" })
      return
    }
    if (this.hasSaveFilterModalTarget) {
      this.resetSaveFilterValidation()
      this.saveFilterModalTarget.showModal()
    }
  }

  cancelSaveFilter() {
    if (this.hasSaveFilterModalTarget) this.saveFilterModalTarget.close()
  }

  resetSaveFilterValidation() {
    if (this.hasSaveFilterLabelTarget) {
      this.saveFilterLabelTarget.setCustomValidity("")
      this.saveFilterLabelTarget.value = ""
      this.saveFilterLabelTarget.classList.remove("input-error")
    }
    if (this.hasSaveFilterHintTarget) {
      this.saveFilterHintTarget.classList.add("hidden")
    }
  }

  clearSaveFilterValidationOnInput() {
    const input = this.saveFilterLabelTarget
    if (!input) return
    const handler = () => {
      input.classList.remove("input-error")
      if (this.hasSaveFilterHintTarget) {
        this.saveFilterHintTarget.classList.add("hidden")
      }
      input.removeEventListener("input", handler)
    }
    input.addEventListener("input", handler)
  }

  async confirmSaveFilter() {
    const input = this.saveFilterLabelTarget
    if (!input) return
    const label = input.value?.trim() ?? ""
    if (!label) {
      input.classList.add("input-error")
      if (this.hasSaveFilterHintTarget) {
        this.saveFilterHintTarget.classList.remove("hidden")
      }
      input.focus()
      this.clearSaveFilterValidationOnInput()
      return
    }
    const filtersArray = this.collectFiltersFromForm()
    if (filtersArray.length === 0) {
      this.cancelSaveFilter()
      return
    }
    const payload = { filters: filtersArray, filter_mode: this.activeMode === "cascade" ? "cascade" : "standard" }
    const encoded = btoa(unescape(encodeURIComponent(JSON.stringify(payload))))
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const res = await fetch(this.savedFiltersPathValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": token || "",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify({
          saved_filter: {
            filter_type: this.filterTypeValue,
            label,
            filters: encoded
          }
        })
      })
      const data = await res.json()
      if (res.ok) {
        this.resetSaveFilterValidation()
        this.cancelSaveFilter()
        this.fetchSavedFilters()
      } else {
        toast(data.errors?.join("\n") || "Failed to save filter.", { type: "error" })
      }
    } catch (_err) {
      toast("Failed to save filter.", { type: "error" })
    }
  }

  loadSavedFilter(event) {
    const encoded = event.currentTarget?.dataset?.encodedFilters
    if (!encoded || !this.basePathValue) return
    const url = new URL(this.basePathValue, window.location.origin)
    url.searchParams.set("filters", encoded)
    const filterMode = event.currentTarget?.dataset?.filterMode
    if (filterMode) url.searchParams.set("filter_mode", filterMode)
    window.location.href = url.toString()
  }

  deleteSavedFilter(event) {
    event.stopPropagation()
    const id = event.currentTarget?.dataset?.savedFilterId
    if (!id || !this.savedFiltersPathValue) return
    this.pendingDeleteId = id
    if (this.hasDeleteFilterModalTarget) this.deleteFilterModalTarget.showModal()
  }

  cancelDeleteFilter() {
    this.pendingDeleteId = null
    if (this.hasDeleteFilterModalTarget) this.deleteFilterModalTarget.close()
  }

  async confirmDeleteFilter() {
    const id = this.pendingDeleteId
    if (!id || !this.savedFiltersPathValue) {
      this.cancelDeleteFilter()
      return
    }
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const res = await fetch(`${this.savedFiltersPathValue}/${id}`, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": token || "",
          "X-Requested-With": "XMLHttpRequest"
        }
      })
      if (res.ok) this.fetchSavedFilters()
    } catch (_err) {
      toast("Failed to delete filter.", { type: "error" })
    } finally {
      this.cancelDeleteFilter()
    }
  }

  collectFiltersFromForm() {
    if (this.activeMode === "cascade") return this.collectCascadeFiltersFromForm()
    return this.collectStandardFiltersFromForm()
  }
}
